import './App.css';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';

import AccountAge from './filters/AccountAge'
import DateRange from './filters/DateRange.js'
import GameTypeSelect from './filters/GameTypeSelect.tsx'
import Maps from './Maps.js'
import Mods from './filters/Mods.tsx'
import TimeGranularity from './TimeGranularity.js'
import PlayerSelect from './filters/PlayerSelect.js'

import { gameTypesAvailable, mapTypeAvailable } from './util.ts'

import {
        filterBarInternalValuesDefaultAtom,
        filterBarInternalValuesAtom,
        loadDateAtom,
        addressFilterAtom,
      } from './state.js'
import { useRecoilState } from 'recoil';
      
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};
function FilterBar(props) {
  const [update, setUpdate] = useState(0);

  const [openWarningStart, setOpenWarningStart] = useState(false);

  const [, setAddressFilter] = useRecoilState(addressFilterAtom);
  const [loadDate, ] = useRecoilState(loadDateAtom);

  const [filterBarInternalValuesDefault, ] = useRecoilState(filterBarInternalValuesDefaultAtom);

  const [internalValues, setInternalValues] = useRecoilState(filterBarInternalValuesAtom);

  const [dateRangeMessage, setDateRangeMessage] = useState(null);

  // State related to changed filters
  const [changedDateRange, setChangedDateRange] = useState(false);
  const [changedGameTypeSelect, setChangedGameTypeSelect] = useState(false);
  const [changedTimeGranularity, setChangedTimeGranularity] = useState(false);
  const [changedAccountAge, setChangedAccountAge] = useState(false);
  const [changedMaps, setChangedMaps] = useState(false);
  const [changedMods, setChangedMods] = useState(false);
  const [changedPlayerId, setChangedPlayerId] = useState(false);

  const makeAddressFilterLocal = (loadDateStart, loadDateEnd) => {
    var addressFilterLocal = ""+
      "&timeGranularity=" + timeGranularity +
      "&startDate=" + ((typeof startDate === "object" && startDate.constructor.name === "Date") ? startDate.valueOf() : startDate) +
      "&endDate=" + ((typeof endDate === "object" && endDate.constructor.name === "Date") ? endDate.valueOf() : endDate) +
      "&loadDateStart=" + loadDateStart +
      "&loadDateEnd=" + loadDateEnd +
      "&accountAgeRangeDays=" + accountAgeRangeDays.join(",") +
      "&mapTypeAvailableChecked=" + mapTypeAvailableChecked.join(",") +
      "&gameTypesChecked=" + gameTypesChecked.join(",") +
      "&mapSize=" + mapSize.join(",") +
      "&playerId=" + playerId +
      "";

    if (gamesChecked.every(v => v === true)) {
      addressFilterLocal += "&gamesChecked=all"
    } else {
      const gc = gamesChecked.join(",");
      if (gc.length<1000) {
        addressFilterLocal += "&gamesChecked=" + gc;
      } else {
        console.log("WARNING: gamesChecked selection too long, not updating address bar");
      }
    }
    return addressFilterLocal;
  };

  const handleUpdate = (evt) => {
    setChangedDateRange(false);
    setChangedGameTypeSelect(false);
    setChangedTimeGranularity(false);
    setChangedAccountAge(false);
    setChangedMaps(false);
    setChangedMods(false);
    setChangedPlayerId(false);
    setUpdate(update+1);

    var addressFilterLocal = makeAddressFilterLocal(loadDate['loadDateStart'], loadDate['loadDateEnd']);

    var previousHash = window.location.hash;
    if(window.location.hash.indexOf("?")!==-1) {
      previousHash = window.location.hash.slice(window.location.hash.indexOf("#"),window.location.hash.indexOf("?"));
    };

    setAddressFilter(addressFilterLocal)
    // Extract the page. The hash value in the address bar must be modified
    // (this is the function that modifies it)
    window.history.pushState("ZKLA view", "", 
      window.location.pathname + 
      previousHash + "?" + addressFilterLocal);
    props.updateCallback({
      startDate: getInternalValue("startDate"),
      endDate: getInternalValue("endDate"),
      timeGranularity: getInternalValue("timeGranularity"),
      accountAgeRangeDays: getInternalValue("accountAgeRangeDays"),
      gamesChecked: getInternalValue("gamesChecked"),
      mapTypeAvailableChecked: getInternalValue("mapTypeAvailableChecked"),
      mapSize: getInternalValue("mapSize"),
      playerId: getInternalValue("playerId"),
      gameTypesAvailable: getInternalValue("gameTypesAvailable"),
      gameTypesChecked: getInternalValue("gameTypesChecked"),
    })
  };
  
  useEffect(() => {
    if((props.jsonBattles!==null) && (props.jsonBattlePlayer!==null) &&
       (props.jsonGames!==null) && (props.jsonMaps!==null) && (props.jsonPlayers!==null)) {
      // After data is loaded, first time we have to force an update (otherwise nothing
      // will be shown). This would interfere for non-independent FilterBar with 
      // setting from global to local (this code could be called after the 
      // invocation of updateCallback there, but before the state is actually set),
      // so we call this: for idependent ones.
      if (props.independent) {
        handleUpdate(null);
      }
    }
  }, [props.jsonBattles, props.jsonBattlePlayer, props.jsonGames, props.jsonMaps, props.jsonPlayers]);

  const getInternalValue = (valueName) => {
    if (props.indexState < internalValues.length) {
      return internalValues[props.indexState][valueName];
    } else {
      return filterBarInternalValuesDefault[valueName];
    }
  }

  const setInternalValue = (valueName, valueValue) => {
    var newInternalValues = [...internalValues];
    if (props.indexState >= internalValues.length) {
      for(var i=internalValues.length;i<props.indexState+1;i++)
        newInternalValues.push(filterBarInternalValuesDefault);
    }
    var newDict = {...newInternalValues[props.indexState]};
    newDict[valueName] = valueValue;
    newInternalValues[props.indexState] = newDict;

    setInternalValues(newInternalValues);
  }

  const startDate = getInternalValue("startDate");
  const endDate = getInternalValue("endDate")
  const timeGranularity = getInternalValue("timeGranularity");
  const accountAgeRangeDays = getInternalValue("accountAgeRangeDays");
  const gamesChecked = getInternalValue("gamesChecked");
  const gameTypesChecked = getInternalValue("gameTypesChecked");
  const mapTypeAvailableChecked = getInternalValue("mapTypeAvailableChecked");
  const mapSize = getInternalValue("mapSize");
  const playerId = getInternalValue("playerId");

  const gameTypesCheckedChange = (idx, evt) => {
    var newGameTypesChecked;
    if (idx===-1) {
      newGameTypesChecked=new Array(gameTypesChecked.length).fill(false);
    } else if(idx===-2) {
      newGameTypesChecked=new Array(gameTypesChecked.length).fill(true);
    } else {
      newGameTypesChecked = [...gameTypesChecked];
      newGameTypesChecked[idx] = evt.target.checked;
    }
    setInternalValue("gameTypesChecked", newGameTypesChecked);
  }

  const modsCheckedChange = (gameId) => {
    var newGamesChecked;
    if (Array.isArray(gameId)) {
      newGamesChecked = gameId;
    } else {
      newGamesChecked = [...gamesChecked];
      if(!newGamesChecked.includes(gameId)) {
        newGamesChecked.push(gameId);
      } else {
        newGamesChecked.splice(newGamesChecked.indexOf(gameId), 1);
      }
    }
    setInternalValue("gamesChecked", newGamesChecked);
  }

  const mapTypeAvailableCheckedChange = (idx, evt) => {
    var newMapTypeAvailableChecked = [...mapTypeAvailableChecked];
    newMapTypeAvailableChecked[idx] = evt.target.checked;
    setInternalValue("mapTypeAvailableChecked", newMapTypeAvailableChecked);
  }

  const handLoadedDataUpdate = () => {
    var addressFilterLocal = makeAddressFilterLocal(  
      ((typeof startDate === "object" && startDate.constructor.name === "Date") ? startDate.valueOf() : startDate),
      ((typeof endDate === "object" && endDate.constructor.name === "Date") ? endDate.valueOf() : endDate)
    );
  
    var previousHash = window.location.hash;
    if(window.location.hash.indexOf("?")!==-1) {
      previousHash = window.location.hash.slice(window.location.hash.indexOf("#"),window.location.hash.indexOf("?"));
    };

    window.location.href=window.location.pathname + previousHash + "?" + addressFilterLocal; 
    window.location.reload();
  };

  return (
    <Stack direction="row" spacing={1}>
      <DateRange
        message={dateRangeMessage}
        changed={changedDateRange}
        startDate={typeof startDate === "number"?new Date(startDate):startDate}
        endDate={typeof endDate === "number"?new Date(endDate):endDate}
        startDateChanged={(newValue) => { 
          if(newValue.unix()*1000<loadDate['loadDateStart']) {
            // TODO: if this is triggered console will print some recursion errors about FocusTrap
            setOpenWarningStart(true);
          }
          setInternalValue("startDate", newValue); 
          setChangedDateRange(true); 
        } }
        endDateChanged={(newValue) => { 
          if (newValue<startDate) {
            setDateRangeMessage(`End date ${newValue} can't be earlier than start date, not updating.`)
            return;
          } else {
            setDateRangeMessage(null);
          }          
          setInternalValue("endDate",newValue); setChangedDateRange(true); 
        } }
      />
      {(!("hideGameModeType" in props) || props.hideGameModeType!==true) && 
        <GameTypeSelect
          changed={changedGameTypeSelect}
          valueChanged={(i,e) => { gameTypesCheckedChange(i,e); setChangedGameTypeSelect(true); } }
          gameTypesChecked={gameTypesChecked}
          gameTypesAvailable={gameTypesAvailable}
        />
      }
      {(!("hideTimeGranularity" in props) || props.hideTimeGranularity!==true) && <TimeGranularity
        changed={changedTimeGranularity}
        valueChanged={(newValue) => { setInternalValue("timeGranularity", newValue); setChangedTimeGranularity(true); } }
        value={timeGranularity}
      />}
      {(!("hideAccountAge" in props) || props.hideAccountAge!==true) &&
        <AccountAge
          changed={changedAccountAge}
          valueChanged={(newValue) => { setInternalValue("accountAgeRangeDays", newValue); setChangedAccountAge(true); } }
          valueDays={accountAgeRangeDays}
        />
      }
      <Mods
        changed={changedMods}
        valueChanged={(i,e) => { modsCheckedChange(i,e); setChangedMods(true); } }
        gamesChecked={gamesChecked}
        gamesAvailable={props.jsonGames}
      />
      <Maps
        changed={changedMaps}
        mapTypeAvailableChecked={mapTypeAvailableChecked}
        mapTypeAvailableCheckedChange={(i,e) => { mapTypeAvailableCheckedChange(i,e); setChangedMaps(true); }}
        mapTypeAvailable={mapTypeAvailable}
        mapSize={mapSize}
        mapSizeChange={(v) => { setInternalValue("mapSize", v); setChangedMaps(true); }}
      />
      {(!("hidePlayerSelect" in props) || props.hidePlayerSelect!==true) &&
        <PlayerSelect
          jsonPlayers={props.jsonPlayers}
          changed={changedPlayerId}
          valueChangedId={(newValue)=> { setInternalValue("playerId", newValue); setChangedPlayerId(true); } }
        />
      }
      <Button
        variant="contained"
        disabled={!(changedTimeGranularity|changedAccountAge|changedMods|changedMaps|changedDateRange|changedPlayerId|changedGameTypeSelect)?true:undefined}
        onClick={handleUpdate}
      >
        Update
      </Button>
      <Modal
        open={openWarningStart}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Currently loaded data (from {new Date(loadDate['loadDateStart']).toISOString().slice(0,10)} to {new Date(loadDate['loadDateEnd']).toISOString().slice(0,10)})
            does not cover the period you selected (from {new Date(getInternalValue("startDate")).toISOString().slice(0,10)} to {new Date(getInternalValue("endDate")).toISOString().slice(0,10)})
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            You can load additional data by pressing the button below (but that
            requires more resources and will take some time depending on network
            speed). If you prefer you can also revert to the date range of the 
            already loaded data.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Button
              variant="contained"
              onClick={handLoadedDataUpdate}
            >Load</Button> 
            <Button
              variant="contained"
              onClick={() => {          
                setInternalValue("startDate", loadDate['loadDateStart']);
                setChangedDateRange(true); 
                setOpenWarningStart(false);
              }}
            >Revert</Button>
          </Stack>
        </Box>
      </Modal>
    </Stack>

  );
}

FilterBar.propTypes = {
  jsonBattles: PropTypes.object,
  jsonBattlePlayer: PropTypes.object,
  jsonGames: PropTypes.object,
  jsonMaps: PropTypes.object,
  jsonPlayers: PropTypes.object,
  updateCallback: PropTypes.func,
  indexState: PropTypes.number,
  hideAccountAge: PropTypes.bool,
  hideGameModeType: PropTypes.bool,
  hideTimeGranularity: PropTypes.bool,
  hidePlayerSelect: PropTypes.bool,
};

export default FilterBar;
